import { useEffect, useState } from "react";
import { Star, ArrowUpRight } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { useSite } from "../../context/SiteContext";
import { api, fileUrl } from "../../lib/api";
import { DynIcon } from "../../lib/icons";
import { SectionHead } from "./Brand";
import { Particles } from "./Effects";

export const Cities = () => {
  const { cities: t } = useLang().t;
  const { site } = useSite();
  return (
    <section id="cities" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="cities-section">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <SectionHead eyebrow={t.eyebrow} title={t.title} sub={t.sub} />
          <div className="flex flex-wrap gap-2" data-testid="cities-list">
            {t.items.map((c) => (
              <span key={c} className="flex items-center gap-1.5 rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-3 py-1.5 text-sm text-zinc-200">
                <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] shadow-[0_0_8px_#D4AF37]" />{c}
              </span>
            ))}
          </div>
          {t.note && <p className="mt-4 text-sm text-zinc-500">{t.note}</p>}
        </div>
        <div className="relative mx-auto w-full max-w-md">
          <div className="absolute inset-8 rounded-full bg-[#D4AF37]/10 blur-3xl" />
          {t.map_image && <img src={fileUrl(t.map_image, site?.performance?.cdn_base_url)} alt="India coverage map" loading="lazy" className="fx-float relative w-full mix-blend-screen" data-testid="cities-map" />}
        </div>
      </div>
    </section>
  );
};

export const Rating = () => {
  const { rating: t } = useLang().t;
  return (
    <section id="ratings" className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8" data-testid="rating-section">
      <SectionHead eyebrow={t.eyebrow} title={t.title} sub={t.sub} />
      <div className="glass-card relative grid gap-10 overflow-hidden p-6 sm:p-10 lg:grid-cols-[220px_1fr_1fr]">
        <Particles testid="rating-particles" />
        <div className="relative text-center lg:text-left">
          <p className="font-serif text-6xl font-extrabold gold-text" data-testid="rating-score">{t.score}</p>
          <div className="mt-2 flex justify-center gap-1 text-[#D4AF37] lg:justify-start">{[...Array(5)].map((_, i) => <Star key={i} size={18} fill={i < Math.round(Number(t.score) || 5) ? "currentColor" : "none"} />)}</div>
          <p className="mt-2 text-sm text-zinc-400">{t.total}</p>
        </div>
        <div className="relative space-y-3" data-testid="rating-bars">
          {t.bars.map((b) => (
            <div key={b.stars} className="flex items-center gap-3 text-sm">
              <span className="flex w-8 items-center gap-1 text-zinc-300">{b.stars}<Star size={12} className="text-[#D4AF37]" fill="currentColor" /></span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/5"><div className="h-full rounded-full bg-gradient-to-r from-[#9A7B2C] to-[#F3D77A]" style={{ width: `${Math.min(100, Number(b.pct) || 0)}%` }} /></div>
              <span className="w-10 text-right text-zinc-500">{b.pct}%</span>
            </div>
          ))}
        </div>
        <div className="relative grid grid-cols-2 gap-3">
          {t.badges.map((b, i) => (
            <div key={i} className="flex flex-col items-center gap-2 rounded-xl border border-[#D4AF37]/25 bg-black/40 p-4 text-center" data-testid={`rating-badge-${i}`}>
              <span className="fx-icon text-[#D4AF37]"><DynIcon name={b.icon} size={22} /></span>
              <span className="text-xs font-medium uppercase tracking-wider text-zinc-200">{b.title}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const LinkItem = ({ href, label, testid }) => (
  <a href={href} data-testid={testid} className="group flex items-center justify-between gap-3 border-b border-white/5 py-3 text-sm text-zinc-300 transition-colors hover:text-[#F3D77A]">
    <span className="min-w-0">{label}</span><ArrowUpRight size={14} className="shrink-0 text-[#D4AF37] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
  </a>
);

export const Guides = () => {
  const { guides: t } = useLang().t;
  const [latest, setLatest] = useState([]);
  useEffect(() => { api.get("/public/posts?kind=blog&limit=4").then(({ data }) => setLatest(data)).catch(() => {}); }, []);
  return (
    <section id="guides" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="guides-section">
      <SectionHead eyebrow={t.eyebrow} title={t.title} />
      <div className={`grid gap-8 sm:grid-cols-2 ${latest.length ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
        {t.columns.map((c, i) => (
          <div key={i} data-testid={`guides-col-${i}`}>
            <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]"><DynIcon name="Sparkles" size={13} />{c.heading}</h3>
            {c.links.map((l, j) => <LinkItem key={j} href={l.href} label={l.label} testid={`guide-link-${i}-${j}`} />)}
          </div>
        ))}
        {latest.length > 0 && (
          <div data-testid="guides-latest">
            <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]"><DynIcon name="Flame" size={13} />{t.latest}</h3>
            {latest.map((p) => <LinkItem key={p.id} href={`/blog/${p.slug}`} label={p.title} testid={`guide-blog-${p.slug}`} />)}
          </div>
        )}
      </div>
    </section>
  );
};
